import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import MailOutlineOutlined from "@mui/icons-material/MailOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import TableTitleBar from "../../../components/TableTitleBar/TableTitleBar";
import {
  Button,
  IconButton,
  Input,
  Table,
  TablePanel,
} from "../../../components/ds";
import {
  deleteOrganizationThunk,
  getOrganizationThunk,
  organizationSelector,
  patchActivationStatus,
  sendOrganizationCredentialsThunk,
  setActivationIsloading,
  setActivationIsNotloading,
  setOrganizationIsDeleting,
  setOrganizationIsNotDeleting,
  setOrganizationIsNotSendingCredentails,
  setOrganizationIsSendingCredentails,
} from "../../../slices/SuperAdmin/OrganisationList.slice";
import { OrganizationServerResponse } from "../../../models/OrganisationServerResponse";
import { Organization } from "../../../models/Organization";
import AddOrganization from "../AddOrganization/AddOrganization";
import {
  showError,
  showSuccess,
} from "../../../helpers/Notifications/Notification";

import organizationsListStyles from "./OrganizationsList.module.scss";
import { getOrganizationTypes } from "../../../slices/Admin/organisationProfile.slice";
import { checkIsSuperAdmin } from "../../../helpers/utils";
import ErrorAlert from "../../../components/UI/ErrorAlert/ErrorAlert";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

function ActivationSwitch({ checked, loading, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-busy={loading || undefined}
      aria-label={label}
      title={checked ? "On" : "Off"}
      disabled={loading}
      className={`ops-switch activate-switch ${
        checked
          ? `${organizationsListStyles.activationStatusSwitchActive} is-on`
          : `${organizationsListStyles.activationStatusSwitchInActive} is-off`
      }`}
      onClick={() => onChange(!checked)}
    >
      <span className="ops-switch-thumb" aria-hidden="true" />
      <span className="ops-switch-text" aria-hidden="true">
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}

export default function OrganizationsList() {
  const dispatch = useDispatch();

  const history = useHistory();

  /**
   * @type {{
   *  isLoading: boolean,
   *  organizationServerResponse: OrganizationServerResponse
   * }}
   */
  const { isLoading, organizationServerResponse, error } =
    useSelector(organizationSelector);
  const { orgTypeMasterData } = useSelector(
    (state) => state.organisationProfile
  );
  const { loading: membershipPortalLoading } = useSelector(
    (state) => state.stateAdminManagement
  );

  /**
   * @type {[ dataSource: Organization ]}
   */
  const [dataSource, setDataSource] = useState([]);
  const [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
  const [paginationTotalPages, setPaginationTotalPages] = useState(0);
  const [paginationCurrentPageSize, setPaginationCurrentPageSize] =
    useState(10);
  const [isOpenAddDrawer, setIsOpenAddDrawer] = useState(false);
  /**
   * @type {[organizationToEdit: ?Organization]}
   */
  const [organizationToEdit, setOrganizationToEdit] = useState(null);
  const location = useLocation();
  const fromStateAdminManagement = location?.state?.stateAdmin || false;
  const userTitle = fromStateAdminManagement ? "member" : "admin";
  const [searchItem, handleSearchChange] = useDebouncedSearch((value) => {
    setPaginationCurrentPage(1);
    handleGetOrganizationThunk(value, 1, paginationCurrentPageSize);
  }, 900);

  useEffect(() => {
    if (
      organizationServerResponse?.data?.shouldOrgListUpdate ||
      fromStateAdminManagement
    ) {
      handleGetOrganizationThunk("", 1, paginationCurrentPageSize);
    } // optimise main organization list API call for data change only
    if (orgTypeMasterData == 0) dispatch(getOrganizationTypes());
  }, []);

  useEffect(() => {
    /**
     * creating datasource for table
     */
    /**
     * @param {Organization} organization
     */
    const d = (organizationServerResponse?.data?.organizationData || []).map(
      (organization, index) => {
        const org = {
          key: organization.id,
          sino:
            (paginationCurrentPage - 1) * paginationCurrentPageSize + index + 1,
          nameofOrg: organization.organizationName,
          organization_type: organization.organization_type,
          emailId: organization.facilitatorEmail,
          nameOfFacilator: organization.facilitatorName,
          activation: handleActivationCellRendering(organization),
          action: handleMoreActionCellRendering(organization),
          raw: organization,
        };

        return org;
      }
    );

    setPaginationTotalPages(
      organizationServerResponse?.data?.organizationCount ?? 0
    );
    setDataSource(d);
  }, [organizationServerResponse, paginationCurrentPage, paginationCurrentPageSize]);

  const handleGetOrganizationThunk = (searchString = "", page, size) => {
    const stateAdminId = fromStateAdminManagement?.id || "";

    dispatch(
      getOrganizationThunk({
        searchString,
        page,
        size,
        stateAdminId,
      })
    );
  };

  /**
   *
   * @param {Organization} organization
   * @returns {JSX.Element}
   */
  const handleActivationCellRendering = (organization) => (
    <div className={organizationsListStyles.activationContainer}>
      <span
        className={
          organization.isActive
            ? organizationsListStyles.activationActive
            : organizationsListStyles.activationCancel
        }
      />
      <ActivationSwitch
        checked={!!organization.isActive}
        loading={organization.isActivationLoading}
        label={`Activate ${organization.organizationName || "organization"}`}
        onChange={() => {
          dispatch(setActivationIsloading(organization));
          dispatch(
            patchActivationStatus({
              organization,
            })
          ).then(() => {
            dispatch(setActivationIsNotloading(organization));
          });
        }}
      />
    </div>
  );

  /**
   *
   * @param {Organization} organization
   */
  const handleMoreActionCellRendering = (organization) => (
    <div style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
      <IconButton
        label="View organization"
        onClick={() => handleViewOnClick(organization)}
      >
        <VisibilityOutlined />
      </IconButton>
      <IconButton
        label="Edit organization"
        onClick={() => handleEditOnClick(organization)}
      >
        <EditOutlined />
      </IconButton>
      <IconButton
        label="Send credentials"
        disabled={organization.isSendingCredentials}
        onClick={() => handleSendCredentialOnClick(organization)}
      >
        <MailOutlineOutlined />
      </IconButton>
    </div>
  );

  /**
   *
   * @param {Organization} organization
   */
  const handleViewOnClick = (organization) => {
    history.push({
      pathname: `/super-admin-organisation-profile/${organization.id}/${organization.facilitatorEmail}`,
      state: {
        fromStateAdminManagement: fromStateAdminManagement,
      },
    });
  };

  /**
   *
   * @param {Organization} organization
   */
  const handleEditOnClick = (organization) => {
    setOrganizationToEdit(organization);
    setIsOpenAddDrawer(true);
  };

  /**
   *
   * @param {Organization} organization
   */
  const handleSendCredentialOnClick = (organization) => {
    dispatch(setOrganizationIsSendingCredentails(organization));
    dispatch(sendOrganizationCredentialsThunk(organization)).then((data) => {
      if (data?.payload?.detail) {
        showError(data?.payload?.detail[0] || "Something went wrong");
        dispatch(setOrganizationIsNotSendingCredentails(organization));
      } else {
        dispatch(setOrganizationIsNotSendingCredentails(organization));
        showSuccess("An email has been sent with the credentials.");
      }
    });
  };

  /**
   *
   * @param {Organization} organization
   */
  const handleDeleteOnClick = (organization) => {
    dispatch(setOrganizationIsDeleting(organization));
    dispatch(deleteOrganizationThunk(organization)).then((data) => {
      if (data?.payload.isAxiosError) {
        showError(
          data?.payload?.response?.data?.message ||
            data?.payload?.response.data?.error?.detail ||
            "Something went wrong"
        );
        dispatch(setOrganizationIsNotDeleting(organization));
      } else {
        dispatch(setOrganizationIsNotDeleting(organization));
        handlePaginationonChange(paginationCurrentPage);
        showSuccess("Organization deleted successfully");
      }
    });
  };

  const handlePaginationonChange = (pageNumber, size = paginationCurrentPageSize) => {
    setPaginationCurrentPage(pageNumber);
    setPaginationCurrentPageSize(size);
    handleGetOrganizationThunk(searchItem, pageNumber, size);
  };

  /**
   *
   * @param {KeyboardEvent} event
   */
  const handleAddOrganizationOnClick = () => {
    setOrganizationToEdit(null);
    setIsOpenAddDrawer(true);
  };

  const handleAddOrganizationOnClose = () => setIsOpenAddDrawer(false);

  const showTable = dataSource.length > 0 || isLoading || searchItem || membershipPortalLoading;
  const totalCount = paginationTotalPages;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationCurrentPageSize) || 1);
  const from =
    totalCount === 0 ? 0 : paginationCurrentPageSize * (paginationCurrentPage - 1) + 1;
  const to = Math.min(paginationCurrentPageSize * paginationCurrentPage, totalCount);
  const emptyText = searchItem
    ? "No matching records found."
    : `No organizations found under the ${userTitle}.`;

  const alertComponent = (
    <ErrorAlert
      errorData={error}
      message={
        dataSource.length == 0 &&
        !searchItem &&
        !isLoading &&
        `No organizations found under the ${userTitle}.`
      }
    />
  );

  const listLoading = isLoading || membershipPortalLoading;

  const tablePanel = (
    <TablePanel
      title="Organizations"
      total={totalCount}
      from={from}
      to={to}
      page={paginationCurrentPage}
      totalPages={totalPages}
      pageSize={paginationCurrentPageSize}
      onPageChange={(page) =>
        handlePaginationonChange(page, paginationCurrentPageSize)
      }
      onPageSizeChange={(size) => handlePaginationonChange(1, size)}
      empty={emptyText}
    >
      <Table className="ops-ds-table">
        <thead>
          <tr>
            <th>SN</th>
            <th>Name of Organization</th>
            <th>Email id</th>
            <th>Organization Type</th>
            <th>Name of Facilitator</th>
            <th>Activation</th>
            <th className="ops-actions" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {dataSource.map((row) => (
            <tr key={row.key}>
              <td>{row.sino}</td>
              <td>{row.nameofOrg}</td>
              <td>{row.emailId}</td>
              <td>{row.organization_type}</td>
              <td>{row.nameOfFacilator}</td>
              <td>{row.activation}</td>
              <td className="ops-actions">{row.action}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TablePanel>
  );

  return (
    <>
      <main>
        <div className={`${organizationsListStyles.container} ops-page`}>
          <TableTitleBar title="List of Organizations">
            <Input
              onKeyUp={handleSearchChange}
              placeholder="Search Organization"
              aria-label="Search Organization"
            />

            {checkIsSuperAdmin() && (
              <Button
                type="button"
                className={organizationsListStyles.btn}
                onClick={handleAddOrganizationOnClick}
              >
                Add organization
              </Button>
            )}
          </TableTitleBar>
          {alertComponent}
          {showTable && (
            <div
              className={`${organizationsListStyles.table} platform-admin-org-management-table`}
            >
              {listLoading && dataSource.length === 0 ? (
                <div className="ops-empty">Loading organizations…</div>
              ) : (
                <>
                  {listLoading ? (
                    <div className="ops-empty" style={{ padding: "12px 16px" }}>
                      Loading organizations…
                    </div>
                  ) : null}
                  {tablePanel}
                </>
              )}
            </div>
          )}
        </div>
      </main>
      <AddOrganization
        open={isOpenAddDrawer}
        onClose={handleAddOrganizationOnClose}
        organization={organizationToEdit}
        isEdit={organizationToEdit && organizationToEdit.id !== 0}
        onRefresh={(type) =>
          handlePaginationonChange(
            type === "add" ? 1 : paginationCurrentPage,
            paginationCurrentPageSize
          )
        }
        organizationTypes={orgTypeMasterData}
        stateAdminId={fromStateAdminManagement?.id || null}
      />
    </>
  );
}
